// frontend/src/components/WithdrawButton.js
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './WithdrawButton.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_ENABLED  = process.env.REACT_APP_TURNSTILE_ENABLED === 'true' && TURNSTILE_SITE_KEY;

function TurnstileWidget({ onVerify, onExpire }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_ENABLED || !ref.current) return;
    const render = () => {
      if (!ref.current || ref.current.dataset.rendered) return;
      ref.current.dataset.rendered = '1';
      window.turnstile.render(ref.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t) => onVerify && onVerify(t),
        'expired-callback': () => onExpire && onExpire(),
        theme: 'light',
      });
    };
    if (window.turnstile) { render(); }
    else {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true; s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    }
    return () => { if (ref.current) delete ref.current.dataset.rendered; };
  }, []);
  if (!TURNSTILE_ENABLED) return null;
  return <div ref={ref} style={{ margin: '12px 0' }} />;
}

const PLACEHOLDER_PREFIXES = ['DNOWALLET', 'DNOWALLETPROVIDED'];
const isPlaceholder = (addr) =>
  !addr || PLACEHOLDER_PREFIXES.some(p => addr.startsWith(p));

const WithdrawButton = ({ onWithdrawSuccess }) => {
  const [showModal, setShowModal]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [message, setMessage]       = useState({ text: '', type: '' });
  const [walletAddress, setWalletAddress] = useState('');
  const [walletError, setWalletError]     = useState('');
  const [publicConfig, setPublicConfig]   = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    if (showModal) {
      fetchAll();
    }
  }, [showModal]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [balRes, cfgRes] = await Promise.all([
        api.get('/withdraw/balance'),
        api.get('/withdraw/config'),
      ]);
      const data = balRes.data;
      setBalanceData(data);
      setPublicConfig(cfgRes.data);

      // Pre-fill wallet if user already has a real one
      if (data.dogecoin_wallet && !isPlaceholder(data.dogecoin_wallet)) {
        setWalletAddress(data.dogecoin_wallet);
      } else {
        setWalletAddress('');
      }
    } catch (error) {
      setMessage({
        text: `❌ ${error.response?.data?.detail || 'Failed to load balance'}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateWallet = (addr) => {
    if (!addr || addr.trim().length === 0) return 'Dogecoin wallet address is required';
    const trimmed = addr.trim();
    if (!trimmed.startsWith('D')) return 'Dogecoin address must start with D';
    if (trimmed.length !== 34) return 'Dogecoin address must be exactly 34 characters';
    if (!/^[A-Za-z0-9]+$/.test(trimmed)) return 'Invalid characters in wallet address';
    return '';
  };

  const handleWithdraw = async () => {
    // Wallet validation
    const walletToUse = walletAddress.trim();
    const err = validateWallet(walletToUse);
    if (err) { setWalletError(err); return; }
    setWalletError('');

    const confirmed = window.confirm(
      `🚀 Confirm Withdrawal\n\n` +
      `Amount: ${balanceData.balance_doge} pts\n` +
      `Wallet: ${walletToUse}\n\n` +
      `Processing time: 10-15 business days.\n\n` +
      `Do you want to proceed?`
    );
    if (!confirmed) return;

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await api.post('/withdraw/request', {
        dogecoin_wallet: walletToUse,
        turnstile_token: turnstileToken
      });
      const data = response.data;
      setMessage({ text: `✅ ${data.message}`, type: 'success' });
      alert(
        `✅ Withdrawal Request Submitted!\n\n` +
        `Amount: ${data.amount} loyalty pts\n` +
        `Wallet: ${data.wallet}\n\n` +
        `Processing time: 10-15 business days.\n` +
        `Check your email for confirmation.`
      );
      setShowModal(false);
      setWalletAddress('');
      if (onWithdrawSuccess) onWithdrawSuccess();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit withdrawal. Please try again.';
      setMessage({ text: `❌ ${msg}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setMessage({ text: '', type: '' });
    setWalletAddress('');
    setWalletError('');
  };

  const hasRealWallet = balanceData?.dogecoin_wallet && !isPlaceholder(balanceData.dogecoin_wallet);
  const allowedCountries = publicConfig?.allowed_countries || [];
  const countryNames = {
    US: 'United States', CA: 'Canada', GB: 'United Kingdom',
    AU: 'Australia', DE: 'Germany', FR: 'France', IN: 'India',
  };
  const countryList = allowedCountries
    .map(c => countryNames[c] || c)
    .join(', ');

  return (
    <>
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); setShowModal(true); }}
        style={{
          color: '#667eea', textDecoration: 'none',
          fontSize: '13px', fontWeight: '600',
          cursor: 'pointer', whiteSpace: 'nowrap'
        }}
        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
      >
        Withdraw →
      </a>

      {showModal && (
        <div className="withdraw-modal-overlay" onClick={resetModal}>
          <div className="withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={resetModal}>✕</button>

            <div className="modal-content">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading...</p>
                </div>
              ) : (
                <>
                  <h2>💰 Withdraw Rewards</h2>

                  {/* Country eligibility notice */}
                  {allowedCountries.length > 0 && (
                    <div className="country-notice">
                      🌍 <strong>Withdrawals available to:</strong> {countryList}
                    </div>
                  )}

                  {balanceData && (
                    <>
                      {/* Balance cards */}
                      <div className="balance-display">
                        <div className="balance-card">
                          <div className="balance-label">Available Balance</div>
                          <div className="balance-amount">{balanceData.balance_doge} pts</div>
                        </div>
                        <div className="stats-grid">
                          <div className="stat-item">
                            <div className="stat-label">Total Rewarded</div>
                            <div className="stat-value">{balanceData.total_earned} pts</div>
                          </div>
                          <div className="stat-item">
                            <div className="stat-label">Articles Read</div>
                            <div className="stat-value">{balanceData.articles_read_count}</div>
                          </div>
                        </div>
                      </div>

                      {/* Wallet address field */}
                      <div className="wallet-field-section">
                        <label className="wallet-field-label">
                          Withdrawal Address
                          {hasRealWallet && (
                            <span className="wallet-locked-badge">🔒 Locked</span>
                          )}
                        </label>

                        {hasRealWallet ? (
                          /* Real wallet already set — readonly */
                          <div className="wallet-readonly">
                            <span className="wallet-readonly-value">{balanceData.dogecoin_wallet}</span>
                            <small className="wallet-hint">
                              Wallet is locked. Contact support to change it.
                            </small>
                          </div>
                        ) : (
                          /* No real wallet — editable */
                          <>
                            <input
                              type="text"
                              placeholder="Enter Dogecoin address (starts with D, 34 chars)"
                              value={walletAddress}
                              onChange={(e) => {
                                setWalletAddress(e.target.value);
                                setWalletError('');
                              }}
                              className={`wallet-input ${walletError ? 'wallet-input-error' : ''}`}
                              maxLength={34}
                              disabled={loading}
                              autoComplete="off"
                              spellCheck={false}
                            />
                            {walletError && (
                              <small className="wallet-error-text">⚠️ {walletError}</small>
                            )}
                            <small className="input-hint">
                              Example: DH5yaieqoZN36fDVciNyRueRGvGLR3mr7L
                            </small>
                          </>
                        )}
                      </div>

                      {/* Status / eligibility message */}
                      {!balanceData.country_allowed ? (
                        <div className="withdrawal-info" style={{ backgroundColor: '#ffebee', borderLeftColor: '#f44336' }}>
                          <p>🚫 Withdrawals are not available in your country ({balanceData.user_country || 'Unknown'}).</p>
                        </div>
                      ) : !balanceData.can_withdraw ? (
                        <div className="withdrawal-info" style={{ backgroundColor: '#fff3e0', borderLeftColor: '#ff9800' }}>
                          <p>⚠️ Minimum: {balanceData.min_withdrawal_amount} pts. You have {balanceData.balance_doge} pts.</p>
                          <strong>Need {Math.max(0, balanceData.min_withdrawal_amount - balanceData.balance_doge).toFixed(3)} more pts!</strong>
                          <br /><small>Keep reading to build your loyalty rewards balance! 📰</small>
                        </div>
                      ) : (
                        <div className="withdrawal-info" style={{ backgroundColor: '#e8f5e9', borderLeftColor: '#4caf50' }}>
                          <p>✅ You can withdraw your rewards! An email will be sent to admin for processing.</p>
                          <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                            ⏱️ Processing time: 10-15 business days
                          </small>
                        </div>
                      )}

                      {/* Turnstile — only shown when eligible */}
                      {balanceData.can_withdraw && (
                        <TurnstileWidget
                          onVerify={(t) => setTurnstileToken(t)}
                          onExpire={() => setTurnstileToken('')}
                        />
                      )}

                      {/* Withdraw button
                          Enabled when: can_withdraw + (has real wallet OR user typed valid wallet) + turnstile ok */}
                      {(() => {
                        const walletReady = hasRealWallet || (walletAddress.trim().length === 34 && walletAddress.trim().startsWith('D'));
                        const turnstileOk = !TURNSTILE_ENABLED || !!turnstileToken;
                        const btnEnabled  = balanceData.can_withdraw && walletReady && turnstileOk && !loading;
                        return (
                          <button
                            onClick={handleWithdraw}
                            disabled={!btnEnabled}
                            className="submit-btn withdraw-submit"
                            style={{
                              opacity: btnEnabled ? 1 : 0.5,
                              cursor:  btnEnabled ? 'pointer' : 'not-allowed',
                              backgroundColor: btnEnabled ? '#4caf50' : '#999'
                            }}
                          >
                            {loading ? 'Processing...' :
                              !balanceData.can_withdraw
                                ? `🔒 Need ${Math.max(0, balanceData.min_withdrawal_amount - balanceData.balance_doge).toFixed(2)} more pts`
                                : !walletReady
                                  ? '🔒 Enter valid wallet to continue'
                                  : `💰 Redeem ${balanceData.balance_doge} pts`
                            }
                          </button>
                        );
                      })()}
                    </>
                  )}

                  {message.text && (
                    <p className={`message ${message.type}`}>{message.text}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawButton;
